import { CACHE_ENDPOINT, V2_ENDPOINT } from "@/consts";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// X402 Endpoint source type
export type X402EndpointSource = "user_import" | "cdp_bazaar";

// ParamDefinition matches BE's CDP-aligned structure
export interface ParamDefinition {
  type: string; // string, number, integer, boolean, array, object
  description?: string;
  required: boolean;
  default?: unknown;
  enum?: unknown[];
  // Validation hints
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  // Complex types
  items?: Record<string, unknown>;
  properties?: Record<string, unknown>;
}

// X402 Endpoint type from backend (matches entity.X402Endpoint)
// Note: x402 endpoints are globally accessible (no organization ownership) as of migration 000019
export interface X402Endpoint {
  id: string;
  baseUrl: string; // Clean URL without query params
  name: string;
  description?: string;
  method: string;

  // Rich param definitions (new CDP-aligned structure)
  queryParams?: Record<string, ParamDefinition>;
  bodyParams?: Record<string, ParamDefinition>;
  headerParams?: Record<string, ParamDefinition>;

  // Default values
  defaultQueryParams?: Record<string, unknown>;
  defaultBody?: Record<string, unknown>;
  defaultHeaders?: Record<string, unknown>;

  // Output schema
  outputSchema?: Record<string, unknown>;

  // Payment info
  price: string;
  maxAllowedPrice?: string;
  tokenAddress: string;
  tokenDecimals: number;
  tokenSymbol?: string;
  network: string;
  payTo: string;

  // Metadata
  category?: string;
  tags?: string[];
  mimeType?: string;

  // Source tracking
  source?: X402EndpointSource;

  // AI discoverability fields
  useCases?: string[];
  inputSummary?: string;
  outputSummary?: string;

  // Examples
  exampleQueryParams?: Record<string, unknown>;
  exampleBody?: unknown;
  exampleOutput?: unknown;

  // Stats
  successCount: number;
  failureCount?: number;
  totalCalls?: number;
  avgResponseMs?: number;
  lastCalledAt?: string;
  lastErrorAt?: string;
  lastError?: string;

  // Spending tracking (tokenAddress -> amount spent)
  spentByToken?: Record<string, string>;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Minimal x402 endpoint metadata for node display and enrichment.
 * Used by mergeX402MetadataToPayload and enrichNodesWithDefinitions.
 */
export interface X402EndpointMetadata {
  name?: string;
  description?: string;
  url?: string;
  price?: string;
  network?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
}

// Probe result type (matches BE's X402EndpointProbeResult)
export interface X402ProbeResult {
  success: boolean;
  error?: string;

  // Core protocol
  price?: string;
  tokenAddress?: string;
  tokenDecimals?: number;
  tokenSymbol?: string;
  tokenName?: string;
  network?: string;
  payTo?: string;
  method?: string;
  description?: string;
  mimeType?: string;

  // Parsed from URL
  baseUrl?: string;
  detectedParams?: Record<string, string>;

  // Rich param definitions (from 402 outputSchema)
  queryParams?: Record<string, ParamDefinition>;
  bodyParams?: Record<string, ParamDefinition>;
  headerParams?: Record<string, ParamDefinition>;
  outputSchema?: Record<string, unknown>;

  // Full accepts array (matches entity.X402PaymentOption)
  accepts?: Array<{
    scheme: string;
    network: string;
    maxAmountRequired: string;
    asset: string;
    payTo: string;
    resource?: string;
    description?: string;
    mimeType?: string;
    maxTimeoutSeconds?: number;
    extra?: {
      name?: string;
      tokenDecimals?: number;
    };
  }>;
}

interface ListEndpointsResponse {
  code: number;
  message: string;
  data: {
    items: X402Endpoint[];
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface SingleEndpointResponse {
  code: number;
  message: string;
  data: X402Endpoint;
}

interface CheckEndpointResponse {
  code: number;
  message: string;
  data: {
    exists: boolean;
    endpoint?: X402Endpoint;
  };
}

interface ProbeEndpointResponse {
  code: number;
  message: string;
  data: X402ProbeResult;
}

// Hook for listing x402 endpoints (public registry)
export function useX402Endpoints(params?: { category?: string; search?: string; limit?: number; offset?: number }) {
  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.set("category", params.category);
  if (params?.search) queryParams.set("search", params.search);
  if (params?.limit) queryParams.set("limit", params.limit.toString());
  if (params?.offset) queryParams.set("offset", params.offset.toString());

  const queryString = queryParams.toString();
  // Use edge cache for full list (no filters), origin for filtered/paginated requests
  const baseUrl = queryString ? V2_ENDPOINT : CACHE_ENDPOINT;
  const url = `${baseUrl}/v1/x402-endpoints${queryString ? `?${queryString}` : ""}`;

  const query = useQuery({
    queryKey: ["x402-endpoints", params],
    queryFn: async () => {
      // Public endpoint - no auth needed
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch x402 endpoints: ${response.statusText}`);
      }

      const data: ListEndpointsResponse = await response.json();

      if (data.code !== 0 && data.code !== 20000) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    endpoints: query.data?.items || [],
    hasMore: query.data?.hasMore || false,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Hook for fetching a single x402 endpoint by ID
export function useX402EndpointById(endpointId: string | null | undefined) {
  const query = useQuery({
    queryKey: ["x402-endpoint", endpointId],
    queryFn: async () => {
      if (!endpointId) return null;

      const response = await fetch(`${V2_ENDPOINT}/v1/x402-endpoints/${endpointId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch endpoint: ${response.statusText}`);
      }

      const data: SingleEndpointResponse = await response.json();

      if (data.code !== 0 && data.code !== 20000) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    enabled: !!endpointId,
    staleTime: 5 * 60 * 1000, // 5 minutes (endpoint data rarely changes)
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    endpoint: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// Hook for checking if endpoint URL exists
export function useCheckX402Endpoint(url: string | null) {
  const query = useQuery({
    queryKey: ["x402-endpoint-check", url],
    queryFn: async () => {
      if (!url) return null;

      const response = await fetch(`${V2_ENDPOINT}/v1/x402-endpoints/by-url?url=${encodeURIComponent(url)}`);

      if (!response.ok) {
        throw new Error(`Failed to check endpoint: ${response.statusText}`);
      }

      const data: CheckEndpointResponse = await response.json();
      return data.data;
    },
    enabled: !!url,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    exists: query.data?.exists || false,
    endpoint: query.data?.endpoint,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// Hook for probing an endpoint (requires auth)
export function useProbeX402Endpoint() {
  const mutation = useMutation({
    mutationFn: async (params: { resourceUrl: string; body?: string; method?: string }) => {
      const { resourceUrl, body, method } = params;
      const response = await authenticatedFetch<ProbeEndpointResponse>(`${V2_ENDPOINT}/v1/x402-endpoints/probe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resourceUrl, body, method }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to probe endpoint: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0 && data.code !== 20000) {
        throw new Error(data.message || "Failed to probe endpoint");
      }

      const result = data.data as X402ProbeResult;

      // Validate probe success field
      if (!result.success) {
        throw new Error(result.error || "Probe returned unsuccessful response");
      }

      return result;
    },
  });

  return {
    probe: mutation.mutateAsync,
    isProbing: mutation.isPending,
    probeResult: mutation.data,
    probeError: mutation.error,
    reset: mutation.reset,
  };
}

// Import params interface for creating an endpoint
export interface ImportX402EndpointParams {
  baseUrl: string; // Clean URL without query params (required by BE)
  name?: string; // Optional: AI generates if empty
  description?: string; // Optional: AI generates if empty
  method?: string;
  category?: string; // Optional: AI generates if empty
  tags?: string[]; // Optional: AI generates if empty

  // Payment info
  price: string;
  maxAllowedPrice?: string; // Price protection (defaults to 5x price on BE)
  tokenAddress: string;
  tokenDecimals?: number;
  tokenSymbol?: string;
  network: string;
  payTo: string;

  // Rich param definitions (new CDP-aligned structure)
  queryParams?: Record<string, ParamDefinition>;
  bodyParams?: Record<string, ParamDefinition>;
  headerParams?: Record<string, ParamDefinition>;

  // Default values
  defaultQueryParams?: Record<string, unknown>;
  defaultBody?: Record<string, unknown>;

  // Output schema
  outputSchema?: Record<string, unknown>;

  // Examples
  exampleQueryParams?: Record<string, unknown>;
  exampleBody?: unknown;
  exampleOutput?: unknown;

  // AI discoverability fields
  useCases?: string[];
  inputSummary?: string;
  outputSummary?: string;

  // Legacy fields (for backwards compatibility)
  inputSchema?: Record<string, unknown>;
  exampleInput?: Record<string, unknown>;
}

// Hook for importing (creating) an endpoint (requires auth + org)
// Name, description, category, and tags are optional - AI will generate them on backend if not provided
export function useImportX402Endpoint() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: ImportX402EndpointParams) => {
      const response = await authenticatedFetch<SingleEndpointResponse>(`${V2_ENDPOINT}/v1/x402-endpoints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to import endpoint: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0 && data.code !== 20000) {
        throw new Error(data.message || "Failed to import endpoint");
      }

      return data.data as X402Endpoint;
    },
    onSuccess: () => {
      // Invalidate the endpoints list to refetch
      queryClient.invalidateQueries({ queryKey: ["x402-endpoints"] });
    },
  });

  return {
    importEndpoint: mutation.mutateAsync,
    isImporting: mutation.isPending,
    importedEndpoint: mutation.data,
    importError: mutation.error,
    reset: mutation.reset,
  };
}

// Atomic import params - simplified for the new atomic endpoint
export interface AtomicImportX402EndpointParams {
  resourceUrl: string;
  method?: string;
  body?: Record<string, unknown>;
  // Optional: User-provided overrides (AI generates if empty)
  name?: string;
  description?: string;
  // Optional: Specify which Turnkey wallet to use for payment.
  // If not provided, uses the organization's default wallet.
  walletAddress?: string;
}

// Payment details returned from atomic import
export interface X402PaymentDetails {
  transaction: string | null; // May be null if x402 server doesn't return x-payment-response header
  payer: string;
  network: string;
  asset: string;
  amountPaid: string;
}

// Atomic import result
export interface AtomicImportX402Result {
  endpoint: X402Endpoint;
  paymentDetails: X402PaymentDetails;
}

interface AtomicImportEndpointResponse {
  code: number;
  message: string;
  data: AtomicImportX402Result;
}

/**
 * Hook for atomic x402 endpoint import (requires auth + org)
 *
 * This combines probe + test payment + AI metadata generation + create/update
 * into a single atomic operation. Prevents data manipulation between steps.
 *
 * Flow:
 * 1. Probes the endpoint to get 402 payment details
 * 2. Makes the payment using org's Turnkey wallet
 * 3. Generates AI metadata from the real response
 * 4. Creates or updates the endpoint in the database
 *
 * If the endpoint already exists, it will be updated with fresh data.
 */
export function useAtomicImportX402Endpoint() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: AtomicImportX402EndpointParams) => {
      const response = await authenticatedFetch<AtomicImportEndpointResponse>(
        `${V2_ENDPOINT}/v1/x402-endpoints/import`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to import endpoint: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0 && data.code !== 20000) {
        throw new Error(data.message || "Failed to import endpoint");
      }

      return data.data as AtomicImportX402Result;
    },
    onSuccess: () => {
      // Invalidate the endpoints list to refetch
      queryClient.invalidateQueries({ queryKey: ["x402-endpoints"] });
    },
  });

  return {
    atomicImport: mutation.mutateAsync,
    isImporting: mutation.isPending,
    importResult: mutation.data,
    importError: mutation.error,
    reset: mutation.reset,
  };
}

// Generated metadata type
export interface X402GeneratedMetadata {
  name: string;
  description: string;
  category: string;
  tags: string[];
  // AI discoverability fields
  useCases?: string[];
  inputSummary?: string;
  outputSummary?: string;
}

interface GenerateMetadataResponse {
  code: number;
  message: string;
  data: X402GeneratedMetadata;
}

// Hook for generating AI metadata (requires auth)
export function useGenerateX402Metadata() {
  const mutation = useMutation({
    mutationFn: async (params: {
      resourceUrl: string;
      method?: string;
      price?: string;
      tokenSymbol?: string;
      network?: string;
      inputSchema?: Record<string, unknown>;
      outputSchema?: Record<string, unknown>;
      exampleInput?: Record<string, unknown>;
      exampleOutput?: Record<string, unknown>;
    }) => {
      const response = await authenticatedFetch<GenerateMetadataResponse>(
        `${V2_ENDPOINT}/v1/x402-endpoints/generate-metadata`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to generate metadata: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0 && data.code !== 20000) {
        throw new Error(data.message || "Failed to generate metadata");
      }

      return data.data as X402GeneratedMetadata;
    },
  });

  return {
    generateMetadata: mutation.mutateAsync,
    isGenerating: mutation.isPending,
    generatedMetadata: mutation.data,
    generateError: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Infer a JSON Schema from a value by examining its structure.
 * Used to generate schema from exampleOutput when outputSchema is not available.
 * This enables the variable picker to show nested fields from actual response data.
 *
 * Exported for use in workflowTransform.ts to avoid code duplication.
 */
export function inferSchemaFromExampleOutput(
  value: unknown,
  maxDepth: number = 3,
  currentDepth: number = 0,
): Record<string, unknown> {
  if (currentDepth >= maxDepth) {
    return { type: "object", additionalProperties: true };
  }

  if (value === null || value === undefined) {
    return { type: "string" };
  }

  if (typeof value === "string") {
    return { type: "string" };
  }

  if (typeof value === "number") {
    return { type: "number" };
  }

  if (typeof value === "boolean") {
    return { type: "boolean" };
  }

  if (Array.isArray(value)) {
    return {
      type: "array",
      items: value.length > 0 ? inferSchemaFromExampleOutput(value[0], maxDepth, currentDepth + 1) : { type: "object" },
    };
  }

  if (typeof value === "object") {
    const properties: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      properties[key] = inferSchemaFromExampleOutput(val, maxDepth, currentDepth + 1);
    }
    return {
      type: "object",
      properties,
    };
  }

  return { type: "string" };
}

// Helper to format price from raw amount
export function formatX402Price(amount: string, decimals: number = 6): string {
  try {
    const value = BigInt(amount);
    const divisor = BigInt(10 ** decimals);
    const whole = value / divisor;
    const fraction = value % divisor;

    if (fraction === BigInt(0)) {
      return `$${whole.toString()}`;
    }

    const fractionStr = fraction.toString().padStart(decimals, "0").replace(/0+$/, "");
    return `$${whole}.${fractionStr}`;
  } catch {
    return "$0";
  }
}

// ============================================================
// Shared utilities for x402 endpoint → action conversion
// ============================================================

/**
 * Minimal endpoint data needed to build a result schema.
 * Used by both x402EndpointToAction and workflowTransform's enrichNodesWithDefinitions.
 */
export interface X402EndpointForResultSchema {
  outputSchema?: unknown;
  exampleOutput?: unknown;
}

/**
 * Build a resultSchema for x402 endpoint nodes.
 * This is the SINGLE SOURCE OF TRUTH for x402 result schema structure.
 *
 * Used by:
 * - x402EndpointToAction: When converting endpoints to action definitions
 * - enrichNodesWithDefinitions: When enriching AI-generated x402 nodes
 *
 * Priority for resource schema:
 * 1. endpoint.outputSchema with properties (explicit schema from CDP)
 * 2. Schema inferred from endpoint.exampleOutput (actual response data)
 * 3. Generic object with additionalProperties: true (fallback)
 */
export function buildX402ResultSchema(endpoint: X402EndpointForResultSchema): Record<string, unknown> {
  let resourceSchema: Record<string, unknown>;

  // Extract schema references with proper typing for cleaner access
  const outputSchema = endpoint.outputSchema as Record<string, unknown> | undefined;
  const modernSchemaProps = outputSchema?.properties;
  const legacySchema = outputSchema?.output as Record<string, unknown> | undefined;
  const legacySchemaProps = legacySchema?.properties;

  if (modernSchemaProps && typeof modernSchemaProps === "object" && Object.keys(modernSchemaProps).length > 0) {
    // Use the explicit outputSchema (modern format: outputSchema.properties)
    resourceSchema = outputSchema as Record<string, unknown>;
  } else if (legacySchemaProps && typeof legacySchemaProps === "object" && Object.keys(legacySchemaProps).length > 0) {
    // Use outputSchema.output as the resource schema (legacy CDP format: outputSchema.output.properties)
    resourceSchema = legacySchema as Record<string, unknown>;
  } else if (
    endpoint.exampleOutput !== null &&
    endpoint.exampleOutput !== undefined &&
    typeof endpoint.exampleOutput === "object"
  ) {
    // Infer schema from the actual response data
    // Note: explicit null check because typeof null === "object"
    resourceSchema = inferSchemaFromExampleOutput(endpoint.exampleOutput);
  } else {
    // Fallback to generic object
    resourceSchema = {
      type: "object",
      description: "The response data from the endpoint (JSON parsed or raw text)",
      additionalProperties: true,
    };
  }

  // Add x-category to resourceSchema for hierarchical grouping
  const categorizedResourceSchema = {
    ...resourceSchema,
    "x-category": "output",
  };

  return {
    type: "object",
    description: "Response from x402 endpoint",
    // Define category metadata for hierarchical variable picker
    "x-categoryOrder": ["output", "payment", "request"],
    "x-categoryLabels": {
      output: "Output",
      payment: "Payment",
      request: "Request",
    },
    properties: {
      status: {
        type: "string",
        description: "Response status (success or failed)",
        enum: ["success", "failed"],
        "x-category": "output",
      },
      statusCode: {
        type: "number",
        description: "HTTP status code",
        "x-category": "output",
      },
      resource: categorizedResourceSchema,
      paymentDetails: {
        type: "object",
        description: "Payment details (present when hadPayment is true)",
        "x-category": "payment",
        properties: {
          transaction: {
            type: "string",
            description: "Transaction hash (may be null if server doesn't return it)",
            nullable: true,
          },
          payer: { type: "string", description: "Payer wallet address" },
          network: { type: "string", description: "Network where payment was made" },
          asset: { type: "string", description: "Token address used for payment" },
          amountPaid: { type: "string", description: "Amount paid in smallest token unit" },
        },
      },
      requestDetails: {
        type: "object",
        description: "Details about the request and payment",
        "x-category": "request",
        properties: {
          url: { type: "string", description: "The URL that was called" },
          method: { type: "string", description: "HTTP method used" },
          hadPayment: { type: "boolean", description: "Whether payment was made for this request" },
        },
      },
    },
  };
}

/**
 * Convert X402Endpoint to ActionTriggerDefinition for the workflow builder.
 * This is the single source of truth for x402 endpoint → action conversion.
 *
 * The payload schema uses grouped params (queryParams, body, headers) with
 * rich schemas from the endpoint's ParamDefinition when available.
 */
export function x402EndpointToAction(endpoint: X402Endpoint): ActionTriggerDefinition {
  // Build properties object with flattened params for proper form rendering
  const properties: Record<string, unknown> = {
    // Internal readOnly fields - hidden from user but used for node display
    endpointId: {
      type: "string",
      default: endpoint.id,
      readOnly: true,
      "x-hidden": true,
    },
    // Display info for the node (hidden from form, shown on canvas)
    _endpointName: {
      type: "string",
      default: endpoint.name,
      readOnly: true,
      "x-hidden": true,
    },
    _endpointDescription: {
      type: "string",
      default: endpoint.description || "",
      readOnly: true,
      "x-hidden": true,
    },
    _endpointUrl: {
      type: "string",
      default: endpoint.baseUrl,
      readOnly: true,
      "x-hidden": true,
    },
    _endpointPrice: {
      type: "string",
      default: endpoint.price,
      readOnly: true,
      "x-hidden": true,
    },
    _endpointNetwork: {
      type: "string",
      default: endpoint.network,
      readOnly: true,
      "x-hidden": true,
    },
    _endpointTokenSymbol: {
      type: "string",
      default: endpoint.tokenSymbol || "USDC",
      readOnly: true,
      "x-hidden": true,
    },
    _endpointTokenDecimals: {
      type: "number",
      default: endpoint.tokenDecimals || 6,
      readOnly: true,
      "x-hidden": true,
    },
    _endpointMaxAllowedPrice: {
      type: "string",
      default: endpoint.maxAllowedPrice || "",
      readOnly: true,
      "x-hidden": true,
    },
  };

  // Helper to flatten params with a prefix for form rendering
  const flattenParams = (
    params: Record<string, ParamDefinition> | undefined,
    prefix: string,
    group: string,
  ): boolean => {
    if (params && Object.keys(params).length > 0) {
      for (const [paramName, paramDef] of Object.entries(params)) {
        properties[`${prefix}.${paramName}`] = {
          type: paramDef.type || "string",
          title: paramName,
          description: paramDef.description,
          default: paramDef.default,
          enum: paramDef.enum,
          "x-group": group,
          "x-required": paramDef.required,
        };
      }
      return true;
    }
    return false;
  };

  // Flatten body params or show generic JSON input for non-GET
  if (!flattenParams(endpoint.bodyParams, "body", "body") && endpoint.method !== "GET") {
    properties.body = {
      type: "object",
      title: "Request Body",
      description: "Request body (JSON). Supports {{variables}}.",
      "x-group": "body",
    };
  }

  // Flatten query params or show generic JSON input for GET
  if (!flattenParams(endpoint.queryParams, "query", "query") && endpoint.method === "GET") {
    properties.queryParams = {
      type: "object",
      title: "Query Parameters",
      description: "URL query parameters (key-value pairs). Supports {{variables}}.",
      additionalProperties: { type: "string" },
      "x-group": "query",
    };
  }

  // Flatten header params or show generic JSON input
  if (!flattenParams(endpoint.headerParams, "header", "headers")) {
    properties.headers = {
      type: "object",
      title: "Custom Headers",
      description: "Custom HTTP headers (key-value pairs). Supports {{variables}}.",
      additionalProperties: { type: "string" },
      "x-group": "headers",
    };
  }

  // Add maxPrice override option
  // Format the default max price for display (uses formatX402Price from this file)
  const formattedDefaultMax = endpoint.maxAllowedPrice
    ? `${formatX402Price(endpoint.maxAllowedPrice, endpoint.tokenDecimals || 6)} ${endpoint.tokenSymbol || "USDC"}`
    : "5× of import price";

  properties.maxPrice = {
    type: "string",
    title: "Max Price Override",
    description: `Maximum price willing to pay per request. Leave empty to use default (${formattedDefaultMax}).`,
    "x-group": "advanced",
  };

  // Build result schema using shared function (single source of truth)
  // Cast to the expected type since buildX402ResultSchema returns a well-structured schema
  const resultSchema = buildX402ResultSchema(endpoint) as {
    type: "object";
    properties: Record<string, unknown>;
  };

  return {
    type: "call-x402-endpoint",
    name: endpoint.name,
    description: endpoint.description || `Call ${endpoint.baseUrl}`,
    platform: "x402",
    payloadSchema: {
      type: "object",
      properties,
      required: ["endpointId"],
    },
    resultSchema,
    // Additional metadata for UI rendering
    connector: { type: "wallet" },
    category: "action" as const, // x402 endpoints are always actions
    tags: ["x402", "payment-gated", ...(endpoint.tags || [])],
    // Endpoint metadata for node display (used by mergeX402MetadataToPayload in onAddNode)
    _endpointMetadata: {
      name: endpoint.name,
      description: endpoint.description,
      url: endpoint.baseUrl,
      price: endpoint.price,
      network: endpoint.network,
      tokenSymbol: endpoint.tokenSymbol || "USDC",
      tokenDecimals: endpoint.tokenDecimals || 6,
    },
  };
}
